import type {
  MarketBoardHtsApiResponse,
  MarketBoardQuotesApiResponse,
} from '@app-types/summaryApi';
import type { IndicesHtsInterval, IndicesMarketGroup } from '@app-types/indices';
import { summaryClient, toApiRequestError } from './client';
import { throwDataApiError } from './dataApiError';

export interface FetchMarketBoardQuotesParams {
  mktGroup: IndicesMarketGroup;
}

export interface FetchMarketBoardHtsParams {
  rics: readonly string[];
  interval: IndicesHtsInterval;
}

const assertSuccessEnvelope = (
  body: MarketBoardQuotesApiResponse | MarketBoardHtsApiResponse | undefined,
  httpStatus: number | undefined,
  fallbackMessage: string,
): void => {
  if (body?.status === 0) return;

  throwDataApiError({
    status: typeof body?.status === 'number' ? body.status : 1,
    message: body?.message,
    reason: body?.reason,
  }, httpStatus, fallbackMessage);
};

export const fetchMarketBoardQuotes = async ({
  mktGroup,
}: FetchMarketBoardQuotesParams): Promise<MarketBoardQuotesApiResponse> => {
  try {
    const response = await summaryClient.get<MarketBoardQuotesApiResponse>(
      '/market-board/quotes',
      { params: { mktGroup } },
    );
    assertSuccessEnvelope(
      response.data,
      response.status,
      'Market Board quotes response is invalid.',
    );
    return response.data;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};

export const fetchMarketBoardHts = async ({
  rics,
  interval,
}: FetchMarketBoardHtsParams): Promise<MarketBoardHtsApiResponse> => {
  try {
    const response = await summaryClient.get<MarketBoardHtsApiResponse>(
      '/market-board/hts',
      {
        params: {
          rics: rics.join(';'),
          i: interval,
        },
      },
    );
    assertSuccessEnvelope(
      response.data,
      response.status,
      'Market Board HTS response is invalid.',
    );
    return response.data;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};
